import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArchitectureOutlined as BEIcon,
  CheckCircleOutline as ValidIcon,
  DescriptionOutlined as A3Icon,
  FolderOutlined as PlansIcon,
  HistoryOutlined as HistoryIcon,
  PlayArrowOutlined as StartIcon,
  ReplayOutlined as BackIcon,
  SendOutlined as SendIcon,
  DoNotDisturbOnOutlined as NoPlanIcon,
  PersonOutline as PersonIcon,
} from '@mui/icons-material';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import usePdfPreview from '../../../hooks/usePdfPreview';
import { usePermissions } from '../../../contexts/PermissionContext';
import TravauxModifiesAlert from '../../comptoir/components/TravauxModifiesAlert';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { bureauEtudesAPI } from '../../../services/api';
import { beKeys, useBureauEtudes, useBureauEtudesMeta } from '../../../hooks/queries/useBureauEtudes';
import { BECandidat, BECard } from '../../../types/bureauEtudes';

const PROD_COLOR = '#FF5722';
const MINI = "Bureau d'études";
const TONE: Record<string, { color: string; bg: string }> = {
  attente: { color: '#B45309', bg: '#FEF3C7' },
  conception: { color: '#3730A3', bg: '#E0E7FF' },
  valide: { color: '#166534', bg: '#DCFCE7' },
  sans_plan: { color: '#475569', bg: '#F1F5F9' },
};
const fD = (d?: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const today = () => new Date().toISOString().slice(0, 10);

/* ---------------- dialogs ---------------- */

/** Who draws it: only the DESSINATEUR users (from /meta). Optional. */
const DessinateurField: React.FC<{
  value: number | ''; onChange: (v: number | '') => void; users: { id: number; full_name: string }[]; sx?: object;
}> = ({ value, onChange, users, sx }) => (
  <TextField select label="Dessinateur" fullWidth sx={sx} value={value}
    onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
    helperText={value ? 'Il reçoit une notification et retrouve la fiche dans son bureau d\'études.'
      : 'Facultatif : sinon le premier dessinateur qui commence la conception la prend.'}>
    <MenuItem value=""><em>Non assigné</em></MenuItem>
    {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>)}
  </TextField>
);

const ReassignerDialog: React.FC<{ card: BECard | null; onClose: () => void; onDone: (c: BECard) => void }> = ({ card, onClose, onDone }) => {
  const { data: meta } = useBureauEtudesMeta(!!card);
  const [value, setValue] = useState<number | ''>('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (card) { setValue(card.dessine_par_id || ''); setErr(''); } }, [card]);
  if (!card) return null;
  const save = async () => {
    setSaving(true); setErr('');
    try { onDone(await bureauEtudesAPI.assigner(card.id, value || null)); }
    catch (e: any) { setErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={() => !saving && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Réassigner · {card.code}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <DessinateurField value={value} onChange={setValue} users={meta?.users || []} sx={{ mt: 1 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <PersonIcon />}
          sx={{ bgcolor: PROD_COLOR, '&:hover': { bgcolor: '#E64A19' } }}>Réassigner</Button>
      </DialogActions>
    </Dialog>
  );
};

const EnvoyerDialog: React.FC<{ open: boolean; onClose: () => void; onDone: (c: BECard) => void }> = ({ open, onClose, onDone }) => {
  const [q, setQ] = useState('');  // what the user searches (drives the list)
  const [text, setText] = useState('');  // what the field shows (the chosen label after a pick)
  const [options, setOptions] = useState<BECandidat[]>([]);
  const [choix, setChoix] = useState<BECandidat | null>(null);
  const [instructions, setInstructions] = useState('');
  const [forcer, setForcer] = useState(false);
  const [motif, setMotif] = useState('');
  const [dessinateur, setDessinateur] = useState<number | ''>('');
  const { data: meta } = useBureauEtudesMeta(open);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const peutForcer = true; // anyone may send / launch without avance, with a motif (same rule as the backend)
  useEffect(() => { if (open) { setChoix(null); setInstructions(''); setErr(''); setQ(''); setText(''); setDessinateur(''); } }, [open]);
  useEffect(() => { setForcer(false); setMotif(''); setErr(''); }, [choix]);
  const cpt = choix?.comptoir && !choix.comptoir.lance ? choix.comptoir : null;  // sending it launches it
  const sansAvance = !!cpt && !cpt.avance;
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => { bureauEtudesAPI.candidats(q).then(setOptions).catch(() => setOptions([])); }, 250);
    return () => clearTimeout(t);
  }, [q, open]);
  const save = async () => {
    if (!choix) { setErr('Choisissez le projet ou le service.'); return; }
    if (sansAvance && !forcer) { setErr(`${choix.code} n'a pas encore d'avance : encaissez d'abord l'avance sur sa fiche Comptoir.`); return; }
    if (sansAvance && !motif.trim()) { setErr("Indiquez le motif de l'envoi sans avance."); return; }
    setSaving(true); setErr('');
    try {
      onDone(await bureauEtudesAPI.envoyer({
        type: choix.type, id: choix.id, instructions: instructions.trim() || null,
        forcer: sansAvance && forcer, motif: sansAvance ? motif.trim() || null : null,
        dessinateur_id: dessinateur || null,
      }));
    }
    catch (e: any) { setErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Envoyer au bureau d'études</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Autocomplete
          options={options} value={choix} onChange={(_, v) => setChoix(v)}
          inputValue={text}
          onInputChange={(_, v, reason) => { setText(v); if (reason !== 'reset') setQ(v); }}
          filterOptions={(x) => x}
          getOptionLabel={(o) => `${o.code} · ${o.nom}`}
          isOptionEqualToValue={(a, b) => a.type === b.type && a.id === b.id}
          renderOption={(props, o) => (
            <li {...props} key={`${o.type}-${o.id}`}>
              <Box>
                <Typography variant="body2"><b>{o.code}</b> · {o.nom}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {o.type === 'projet' ? 'Projet' : 'Service'}{o.comptoir ? ' · Comptoir' : ''}{o.client ? ` · ${o.client}` : ''}
                  {o.comptoir && !o.comptoir.lance && !o.comptoir.avance && <Box component="span" sx={{ color: '#B45309', fontWeight: 600 }}> · sans avance</Box>}
                </Typography>
              </Box>
            </li>
          )}
          ListboxProps={{ style: { maxHeight: 440 } }}
          renderInput={(p) => <TextField {...p} label="Projet / service" placeholder="Code ou nom" sx={{ mt: 1 }}
            helperText={`${options.length} projet(s) / service(s) pas encore au bureau d'études${q.trim() ? ' pour cette recherche' : ''}`} />}
          noOptionsText="Aucun projet / service à envoyer"
        />
        {choix?.comptoir?.lance && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {choix.code} vient du Comptoir et a été lancé en production avant le bureau d'études : il rejoint simplement le bureau d'études.
          </Alert>
        )}
        {cpt && !sansAvance && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {choix!.code} vient du Comptoir : l'envoyer au bureau d'études le <b>lance aussi en production</b> (comme « Lancer en production » sur sa fiche).
          </Alert>
        )}
        {sansAvance && (
          <Alert severity="warning" sx={{ mt: 2 }}
            action={<Button color="inherit" size="small" onClick={() => navigate(`/comptoir/services/${cpt!.id}`)}>Ouvrir la fiche</Button>}>
            {choix!.code} vient du Comptoir et n'a pas encore d'avance : encaissez d'abord l'avance sur sa fiche.
            {peutForcer && (
              <FormControlLabel sx={{ display: 'flex', mt: 0.5 }} control={<Checkbox checked={forcer} onChange={(e) => setForcer(e.target.checked)} />}
                label="Envoyer quand même sans avance" />
            )}
          </Alert>
        )}
        {sansAvance && forcer && (
          <TextField label="Motif de l'envoi sans avance" required fullWidth sx={{ mt: 2 }} value={motif} onChange={(e) => setMotif(e.target.value)}
            placeholder="ex : client habituel, avance promise demain" />
        )}
        <DessinateurField value={dessinateur} onChange={setDessinateur} users={meta?.users || []} sx={{ mt: 2 }} />
        <TextField label="Instructions pour le bureau d'études" fullWidth multiline minRows={3} sx={{ mt: 2 }}
          value={instructions} onChange={(e) => setInstructions(e.target.value)}
          placeholder="Ce que le client a demandé, contraintes, pièces de référence…" helperText="Facultatif — imprimé sur la fiche A3" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={saving || (sansAvance && !forcer)} startIcon={saving ? <CircularProgress size={16} /> : <SendIcon />}
          sx={{ bgcolor: PROD_COLOR, '&:hover': { bgcolor: '#E64A19' } }}>Envoyer</Button>
      </DialogActions>
    </Dialog>
  );
};

const ValiderDialog: React.FC<{ card: BECard | null; onClose: () => void; onDone: (c: BECard) => void }> = ({ card, onClose, onDone }) => {
  const [d, setD] = useState(today());
  const [obs, setObs] = useState('');
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (card) { setD(today()); setObs(card.observations || ''); setOk(false); setErr(''); }
  }, [card]);
  if (!card) return null;
  const save = async () => {
    if (!ok) { setErr('Cochez « Je valide le plan technique » pour confirmer.'); return; }
    setSaving(true); setErr('');
    try { onDone(await bureauEtudesAPI.valider(card.id, { date_dessin: d || null, observations: obs.trim() || null, confirme: true })); }
    catch (e: any) { setErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Valider le plan · {card.code}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Typography variant="body2" sx={{ mb: 2 }}><b>{card.nom}</b>{card.client ? ` · ${card.client}` : ''} · livraison {fD(card.deadline)}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={7}>
            {/* chosen at the launch / send (or « Commencer »): no choice here */}
            <TextField label="Dessiné par" fullWidth value={card.dessine_par || 'Vous'} InputProps={{ readOnly: true }}
              helperText={card.dessine_par ? 'Le dessinateur assigné' : 'Aucun dessinateur assigné : le plan est à votre nom'} />
          </Grid>
          <Grid item xs={5}>
            <TextField label="Date du dessin" type="date" fullWidth value={d} onChange={(e) => setD(e.target.value)}
              InputLabelProps={{ shrink: true }} inputProps={{ max: today() }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Observations" fullWidth multiline minRows={2} value={obs} onChange={(e) => setObs(e.target.value)}
              placeholder="Points d'attention pour l'atelier, cotes critiques…" />
          </Grid>
        </Grid>
        {card.plans > 0
          ? <Alert severity="info" sx={{ mt: 2 }}>{card.plans} plan(s) technique(s) déposé(s).</Alert>
          : <Alert severity="warning" sx={{ mt: 2 }}>Aucun plan technique n'est déposé pour {card.code} : déposez-les pour qu'ils arrivent chez les opérateurs.</Alert>}
        <FormControlLabel sx={{ mt: 1 }} control={<Checkbox checked={ok} onChange={(e) => setOk(e.target.checked)} />}
          label="Je valide le plan technique : le projet peut passer à la planification." />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Annuler</Button>
        <Button variant="contained" color="success" onClick={save} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <ValidIcon />}>Valider le plan</Button>
      </DialogActions>
    </Dialog>
  );
};

const MotifDialog: React.FC<{
  open: { card: BECard; kind: 'sans_plan' | 'renvoyer' } | null; onClose: () => void; onDone: (c: BECard) => void;
}> = ({ open, onClose, onDone }) => {
  const [motif, setMotif] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setMotif(''); setErr(''); } }, [open]);
  if (!open) return null;
  const sansPlan = open.kind === 'sans_plan';
  const save = async () => {
    if (!motif.trim()) { setErr(sansPlan ? 'Indiquez pourquoi aucun plan n\'est nécessaire.' : 'Indiquez pourquoi le plan repart en conception.'); return; }
    setSaving(true); setErr('');
    try { onDone(sansPlan ? await bureauEtudesAPI.sansPlan(open.card.id, motif.trim()) : await bureauEtudesAPI.renvoyer(open.card.id, motif.trim())); }
    catch (e: any) { setErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{sansPlan ? 'Pas de plan nécessaire' : 'Renvoyer en conception'} · {open.card.code}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {sansPlan
            ? 'Le travail est assez simple pour partir sans dessin : il sort du bureau d\'études et la planification n\'est plus avertie.'
            : 'Le plan repasse « En cours de conception » : la planification est de nouveau avertie jusqu\'à la prochaine validation.'}
        </Typography>
        <TextField label="Motif" required fullWidth autoFocus value={motif} onChange={(e) => setMotif(e.target.value)}
          placeholder={sansPlan ? 'ex : pliage simple d\'après échantillon du client' : 'ex : le client a changé la cote de 120 à 140'} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : undefined}
          sx={{ bgcolor: PROD_COLOR, '&:hover': { bgcolor: '#E64A19' } }}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
};

const HistoriqueDialog: React.FC<{ card: BECard | null; onClose: () => void }> = ({ card, onClose }) => {
  const [lines, setLines] = useState<{ date: string; description: string; user_name: string | null }[] | null>(null);
  useEffect(() => {
    setLines(null);
    if (card) bureauEtudesAPI.historique(card.id).then(setLines).catch(() => setLines([]));
  }, [card]);
  if (!card) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Historique · {card.code}</DialogTitle>
      <DialogContent>
        {!lines ? <CircularProgress size={22} /> : lines.length === 0 ? <Typography color="text.secondary">Aucune ligne.</Typography> : lines.map((l, i) => (
          <Box key={i} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="body2">{l.description}</Typography>
            <Typography variant="caption" color="text.secondary">{new Date(l.date).toLocaleString('fr-FR')} · {l.user_name || '—'}</Typography>
          </Box>
        ))}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fermer</Button></DialogActions>
    </Dialog>
  );
};

/** Every validated / « pas de plan » job: the page column only keeps the latest ones. */
const TousLesValides: React.FC<{
  open: boolean; onClose: () => void; renderCard: (c: BECard) => React.ReactNode; stamp: unknown; // the board: reload with it
}> = ({ open, onClose, renderCard, stamp }) => {
  const PAGE = 20;
  const [q, setQ] = useState('');
  const [items, setItems] = useState<BECard[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const load = async (search: string, skip: number) => {
    setLoading(true);
    try {
      const r = await bureauEtudesAPI.valides({ q: search.trim(), skip, limit: PAGE });
      setTotal(r.total);
      setItems((prev) => (skip ? [...prev, ...r.items] : r.items));
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => { load(q, 0); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open, stamp]);
  useEffect(() => { if (!open) { setQ(''); setItems([]); } }, [open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Tous les plans validés</DialogTitle>
      <DialogContent>
        <TextField fullWidth size="small" value={q} onChange={(e) => setQ(e.target.value)} sx={{ mt: 1, mb: 2 }}
          placeholder="Réf., désignation, client, dessiné par, observations…" autoFocus />
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          {total} plan(s){q.trim() ? ' trouvé(s)' : ' validé(s) ou sans plan'}, du plus récent au plus ancien
        </Typography>
        {items.map(renderCard)}
        {!loading && items.length === 0 && <Typography color="text.secondary">Aucun plan.</Typography>}
        {loading && <CircularProgress size={22} />}
        {!loading && items.length < total && (
          <Button onClick={() => load(q, items.length)}>Afficher plus ({total - items.length} restant(s))</Button>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fermer</Button></DialogActions>
    </Dialog>
  );
};

/* ---------------- page ---------------- */

/** Production › Bureau d'études: plans to draw and validate before planning (after the prototype). */
const BureauEtudes: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission('Production', 'add', MINI);
  const canEdit = hasPermission('Production', 'edit', MINI);
  const canPlans = hasPermission('Production', 'view', 'Dessins');
  const { data, isLoading, error } = useBureauEtudes();
  const { alertState, showError, showSuccess, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const [envoi, setEnvoi] = useState(false);
  const [valider, setValider] = useState<BECard | null>(null);
  const [motif, setMotif] = useState<{ card: BECard; kind: 'sans_plan' | 'renvoyer' } | null>(null);
  const [hist, setHist] = useState<BECard | null>(null);
  const [reassigner, setReassigner] = useState<BECard | null>(null);
  const [tous, setTous] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const t = today();

  const done = (msg: string) => (c: BECard) => {
    setEnvoi(false); setValider(null); setMotif(null); setReassigner(null);
    qc.invalidateQueries({ queryKey: ['production'] });
    showSuccess(`${c.code} : ${msg}`);
  };
  const commencer = async (c: BECard) => {
    setBusy(c.id);
    try { done('conception commencée')(await bureauEtudesAPI.commencer(c.id)); }
    catch (e: any) { showError(extractErrorMessage(e)); } finally { setBusy(null); }
  };
  const ficheA3 = (c: BECard) => openPreview({
    getBlob: async () => (await import('../../../utils/bureauEtudesPdfReactPdf')).getFicheBEPDFBlob(c),
    fileName: `Fiche_BE_${c.code}.pdf`,
  });
  const plans = (c: BECard) => navigate(`/production/dessins?type=${c.type}&id=${c.entity_id}&code=${encodeURIComponent(c.code)}&nom=${encodeURIComponent(c.nom)}`);

  const card = (c: BECard) => {
    const late = c.deadline && c.deadline < t && !['valide', 'sans_plan'].includes(c.statut);
    const tone = TONE[c.statut];
    return (
      <Paper key={c.id} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: PROD_COLOR, flex: 1 }}>{c.code}</Typography>
          {c.urgent && <Chip label="Urgent" size="small" color="error" variant="outlined" sx={{ height: 20 }} />}
          <Chip label={c.statut_label} size="small" sx={{ height: 22, fontWeight: 600, color: tone.color, bgcolor: tone.bg }} />
        </Stack>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.nom}</Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {c.type === 'projet' ? 'Projet' : 'Service'}{c.client ? ` · ${c.client}` : ''}
        </Typography>
        <Typography variant="caption" component="div" sx={{ color: late ? 'error.main' : 'text.secondary', fontWeight: late ? 600 : undefined }}>
          Livraison {fD(c.deadline)}{c.envoye_le ? ` · envoyé le ${fD(c.envoye_le)}${c.auto ? ` (lancé depuis le Comptoir${c.envoye_par ? ` par ${c.envoye_par}` : ''})` : c.envoye_par ? ` par ${c.envoye_par}` : ''}` : ''}
        </Typography>
        {['attente', 'conception'].includes(c.statut) && (
          <Typography variant="caption" component="div" sx={{ fontWeight: 600, color: c.dessine_par ? 'text.primary' : 'text.secondary' }}>
            Dessinateur : {c.dessine_par || 'non assigné'}
          </Typography>
        )}
        <Typography variant="caption" component="div"
          color={c.plans || ['valide', 'sans_plan'].includes(c.statut) ? 'text.secondary' : 'warning.dark'}>
          {c.plans ? `${c.plans} plan(s) dans l'archive` : 'Aucun plan déposé'}
        </Typography>
        {c.instructions && <Alert severity="info" icon={false} sx={{ mt: 1, py: 0, '& .MuiAlert-message': { fontSize: 13 } }}>{c.instructions}</Alert>}
        <TravauxModifiesAlert revoir={c.revoir} sx={{ mt: 1, '& .MuiAlert-message': { fontSize: 13 } }}
          onVu={canEdit ? async () => {
            const r = await bureauEtudesAPI.travauxVus(c.id);
            qc.invalidateQueries({ queryKey: ['comptoir'] });  // the Comptoir pages show it too
            done('modification des travaux vue')(r);
          } : undefined} />
        {c.statut === 'valide' && (
          <Typography variant="caption" color="success.dark" component="div" sx={{ mt: 0.5 }}>
            Validé le {fD(c.valide_le)}{c.valide_par ? ` par ${c.valide_par}` : ''}{c.dessine_par ? ` · dessiné par ${c.dessine_par}` : ''}
            {c.observations ? ` · ${c.observations}` : ''}
          </Typography>
        )}
        {c.statut === 'sans_plan' && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>Sans plan — {c.motif}</Typography>
        )}
        {c.statut === 'conception' && c.motif && (
          <Typography variant="caption" color="warning.dark" component="div" sx={{ mt: 0.5 }}>Renvoyé : {c.motif}</Typography>
        )}
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <Button size="small" startIcon={<A3Icon />} onClick={() => ficheA3(c)}>Fiche A3</Button>
          {canPlans && <Button size="small" startIcon={<PlansIcon />} onClick={() => plans(c)}>Plans</Button>}
          {canAdd && c.statut === 'attente' && (
            <Button size="small" variant="outlined" startIcon={busy === c.id ? <CircularProgress size={14} /> : <StartIcon />}
              disabled={busy === c.id} onClick={() => commencer(c)}>Commencer</Button>
          )}
          {canEdit && ['attente', 'conception'].includes(c.statut) && (
            <>
              <Button size="small" variant="contained" color="success" startIcon={<ValidIcon />} onClick={() => setValider(c)}>Valider le plan</Button>
              <Button size="small" startIcon={<NoPlanIcon />} onClick={() => setMotif({ card: c, kind: 'sans_plan' })}>Pas de plan</Button>
            </>
          )}
          {canEdit && ['valide', 'sans_plan'].includes(c.statut) && (
            <Button size="small" startIcon={<BackIcon />} onClick={() => setMotif({ card: c, kind: 'renvoyer' })}>Renvoyer en conception</Button>
          )}
          {data?.peut_assigner && ['attente', 'conception'].includes(c.statut) && (
            <Button size="small" startIcon={<PersonIcon />} onClick={() => setReassigner(c)}>Réassigner</Button>
          )}
          <Button size="small" color="inherit" startIcon={<HistoryIcon />} onClick={() => setHist(c)}>Historique</Button>
        </Stack>
      </Paper>
    );
  };

  const col = (title: string, list: BECard[] | undefined, empty: string, extra?: string, action?: React.ReactNode) => (
    <Grid item xs={12} md={4}>
      <Paper sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover', height: '100%' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, px: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>{title}</Typography>
          {action}
          <Chip label={extra || list?.length || 0} size="small" />
        </Stack>
        {isLoading ? <><Skeleton height={120} /><Skeleton height={120} /></>
          : list?.length ? list.map(card) : <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>{empty}</Typography>}
      </Paper>
    </Grid>
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-start' }} spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ maxWidth: 820 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <BEIcon sx={{ color: PROD_COLOR }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Bureau d'études</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Les projets et services arrivent ici quand on les envoie au bureau d'études (ou avec « Lancer en production » pour le Comptoir). Le BE imprime la
            fiche A3, dépose les plans dans Plans techniques puis valide le plan. Tant qu'il n'est pas validé, la planification est bloquée.
          </Typography>
        </Box>
        {canAdd && (
          <Button variant="contained" startIcon={<SendIcon />} onClick={() => setEnvoi(true)}
            sx={{ bgcolor: PROD_COLOR, '&:hover': { bgcolor: '#E64A19' }, whiteSpace: 'nowrap', flexShrink: 0 }}>
            Envoyer au bureau d'études
          </Button>
        )}
      </Stack>

      {!!error && <Alert severity="error" sx={{ mb: 2 }}>{extractErrorMessage(error)}</Alert>}
      {data?.vue === 'mes' && (
        <Alert severity="info" sx={{ mb: 2 }}>Vous voyez vos fiches et celles qui ne sont encore assignées à personne.</Alert>
      )}

      <Grid container spacing={2}>
        {col('À traiter', data?.attente, 'Aucun projet en attente.')}
        {col('En conception', data?.conception, 'Aucun plan en cours.')}
        {col('Plans validés récemment', data?.valides, 'Aucun plan validé.',
          data && data.valides_total > data.valides.length ? `${data.valides.length} / ${data.valides_total}` : undefined,
          data && data.valides_total > 0
            ? <Button size="small" onClick={() => setTous(true)} sx={{ color: PROD_COLOR, whiteSpace: 'nowrap' }}>Voir tout</Button>
            : undefined)}
      </Grid>

      <EnvoyerDialog open={envoi} onClose={() => setEnvoi(false)}
        // a Comptoir job sent from here is launched too: its Comptoir pages change as well
        onDone={(c) => { qc.invalidateQueries({ queryKey: ['comptoir'] }); done('envoyé au bureau d\'études')(c); }} />
      <ValiderDialog card={valider} onClose={() => setValider(null)} onDone={done('plan validé, prêt pour la planification')} />
      <MotifDialog open={motif} onClose={() => setMotif(null)} onDone={done(motif?.kind === 'sans_plan' ? 'sans plan' : 'renvoyé en conception')} />
      <HistoriqueDialog card={hist} onClose={() => setHist(null)} />
      <ReassignerDialog card={reassigner} onClose={() => setReassigner(null)} onDone={done('dessinateur changé')} />
      <TousLesValides open={tous} onClose={() => setTous(false)} renderCard={card} stamp={data} />
      <AlertDialog open={alertState.open} type={alertState.type} title={alertState.title} message={alertState.message}
        onClose={hideAlert} onConfirm={alertState.onConfirm} confirmText={alertState.confirmText}
        cancelText={alertState.cancelText} showCancel={alertState.showCancel} />
      {pdfPreviewElement}
    </Box>
  );
};

export default BureauEtudes;
